import { BlockStack, Box, LegacyCard, LegacyStack, Text } from '@shopify/polaris'
import React, { useContext } from 'react'
import useSWR from 'swr'

import { backendFetchResultData, SubscriptionContractSubscriptionStatus } from '../common/api'
import { anyWindow, CenteredSpinner } from '../common/helpers'
import { HelpTitle } from '../common/HelpIcon'
import { basicStatsEmptyData } from '../common/new-user-test-data'
import { SettingsContext } from '../settings-page/api'
import { DashboardAnalyticsStartDateInfo } from './DashboardSettingsModal'

interface BasicStatCardProps {
  title: string
  tooltip: string
  loading: boolean
  children?: any
  filterLabel?: string
  text?: string | number
}

export const BasicStatCard = (props: BasicStatCardProps) => (
  <LegacyCard
    title={<HelpTitle title={props.title} children={props.tooltip}></HelpTitle>}
    sectioned
    actions={[{ content: props.filterLabel, disabled: true }]}
  >
    {props.loading && <CenteredSpinner />}
    {!props.loading && (
      <>
        {props.text !== undefined && props.text !== null && (
          <Text variant="headingLg" as="p">
            {props.text}
          </Text>
        )}
        {props.children}
      </>
    )}
  </LegacyCard>
)

export interface BasicStatsResponse {
  totalSubscriptions: number
  subscriptionCountByStatus: { [key in SubscriptionContractSubscriptionStatus]: number }
  totalOrders: number
  totalCustomers: number
  activeCustomers: number
}

const fetchBasicStats = () => {
  if (anyWindow.newUserTest) {
    return Promise.resolve(basicStatsEmptyData)
  }

  return backendFetchResultData<BasicStatsResponse>('GET', '/dashboard/basic-stats')
}
export const BasicStats = () => {
  const userSettings = useContext(SettingsContext)

  const { data } = useSWR<BasicStatsResponse>(
    ['dashboard-stats', userSettings?.analytics_start_date],
    fetchBasicStats,
    {
      revalidateOnFocus: false,
    }
  )

  const loading = !data

  return (
    <LegacyStack vertical>
      <Box paddingInline={{ xs: '400', sm: '0', md: '0', lg: '0', xl: '0' }}>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h1">
            Overview
          </Text>
          <DashboardAnalyticsStartDateInfo />
        </BlockStack>
      </Box>
      <LegacyStack distribution="fillEvenly">
        <BasicStatCard
          title="Total subscriptions"
          tooltip="Number of all subscription contracts across various statuses."
          loading={loading}
          text={data?.totalSubscriptions}
        />
        <BasicStatCard
          title="Active subscriptions"
          tooltip="Number of subscriptions currently in an active status. This includes subscriptions retrying failed payments."
          loading={loading}
          text={data?.subscriptionCountByStatus[SubscriptionContractSubscriptionStatus.Active] ?? 0}
        />
        <BasicStatCard
          title="Paused subscriptions"
          tooltip="Number of subscriptions temporarily on hold, marked as paused."
          loading={loading}
          text={data?.subscriptionCountByStatus[SubscriptionContractSubscriptionStatus.Paused] ?? 0}
        />
      </LegacyStack>
      <LegacyStack distribution="fillEvenly">
        <BasicStatCard
          title="Cancelled subscriptions"
          tooltip="Number of subscriptions that have been terminated or cancelled by a customer or admin."
          loading={loading}
          text={
            data?.subscriptionCountByStatus[SubscriptionContractSubscriptionStatus.Cancelled] ?? 0
          }
        />
        <BasicStatCard
          title="Expired subscriptions"
          tooltip="Number of subscriptions that have reached the end of their term successfully."
          loading={loading}
          text={
            data?.subscriptionCountByStatus[SubscriptionContractSubscriptionStatus.Expired] ?? 0
          }
        />
        <BasicStatCard
          title="Failed subscriptions"
          tooltip="Number of subscriptions that were cancelled due to failing the maximum retry attempts set in your configuration. Excludes subscriptions currently undergoing the dunning process."
          loading={loading}
          text={data?.subscriptionCountByStatus[SubscriptionContractSubscriptionStatus.Failed] ?? 0}
        />
      </LegacyStack>
      <LegacyStack distribution="fillEvenly">
        <BasicStatCard
          title="Total orders"
          tooltip="The total number of orders associated with subscription plans, including the initial checkout order. Please note that our reporting includes initial orders, which may differ from Shopify sales channel reporting for our app."
          loading={loading}
          text={data?.totalOrders}
        />
        <BasicStatCard
          title="Total customers"
          tooltip="Number of all subscription customers, including those with active and inactive subscriptions."
          loading={loading}
          text={data?.totalCustomers}
        />
        <BasicStatCard
          title="Active customers"
          tooltip="Number of customers with one or more active subscriptions."
          loading={loading}
          text={data?.activeCustomers}
        />
      </LegacyStack>
    </LegacyStack>
  )
}
